import { useState } from 'react';
import { ChevronLeftIcon, EyeOffIcon } from '../components/Icons';
import { login, forgotPassword, verifyOtp, resetPassword } from '../db';
import { toast } from '../toast';

function BrandPanel() {
  return (
    <div className="hidden lg:flex flex-col justify-between bg-[#F47B20] p-12 text-white">
      <div className="flex items-center gap-3">
        <img src="/logo.png" alt="Manna Bridal Logo" className="h-12 object-contain" />
      </div>
      <div className="space-y-4">
        <p className="text-white/70 text-sm font-medium uppercase tracking-wider">Welcome back</p>
        <h2 className="text-4xl font-black leading-tight">Great to see<br />you again.</h2>
        <p className="text-white/80 text-base leading-relaxed">Log in to track your orders and manage your account.</p>
      </div>
      <div className="space-y-3">
        {['Secure & encrypted login', 'Manage your orders instantly', 'Access exclusive bridal collections'].map(item => (
          <div key={item} className="flex items-center gap-3">
            <div className="w-5 h-5 bg-white/25 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="text-white/90 text-sm">{item}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Forgot Password 3-step modal ─────────────────────────────────────────────
function ForgotPasswordModal({ onClose }) {
  const [step, setStep]       = useState(1); // 1=email, 2=otp, 3=newpass
  const [email, setEmail]     = useState('');
  const [otp, setOtp]         = useState('');
  const [pass, setPass]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState('');
  const [showP, setShowP]     = useState(false);

  const inputCls = "w-full px-4 py-3.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all";

  const handleSendOtp = async () => {
    if (!email) { setErr('Please enter your email.'); return; }
    setErr(''); setBusy(true);
    try {
      await forgotPassword(email);
      setStep(2);
      toast.success('OTP sent! Check your email (and spam folder if you don\'t see it).');
    } catch (e) {
      setErr('Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) { setErr('Enter the 6-digit OTP.'); return; }
    setErr(''); setBusy(true);
    try {
      await verifyOtp(email, otp);
      setStep(3);
    } catch (e) {
      setErr('That code is invalid or has expired. Please try again.');
    } finally { setBusy(false); }
  };

  const handleReset = async () => {
    if (!pass || pass.length < 8) { setErr('Password must be at least 8 characters.'); return; }
    if (pass !== confirm) { setErr('Passwords do not match.'); return; }
    setErr(''); setBusy(true);
    try {
      await resetPassword(email, otp, pass, confirm);
      toast.success('Password reset! Please log in.');
      onClose();
    } catch (e) {
      setErr('Could not reset password. Please try again or contact support.');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-sm p-7 shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex justify-between items-start mb-5">
          <div>
            <h3 className="text-lg font-black text-[#1A1A2E]">
              {step === 1 ? 'Forgot Password' : step === 2 ? 'Enter OTP' : 'New Password'}
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              {step === 1 ? 'We\'ll send a 6-digit code to your email.'
               : step === 2 ? `Code sent to ${email}. Check spam if you don't see it.`
               : 'Choose a strong new password.'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl leading-none">✕</button>
        </div>

        {/* Step indicator */}
        <div className="flex gap-1.5 mb-5">
          {[1,2,3].map(s => (
            <div key={s} className={`h-1 flex-1 rounded-full transition-colors ${step >= s ? 'bg-[#F47B20]' : 'bg-gray-100'}`} />
          ))}
        </div>

        {/* Step 1 — email */}
        {step === 1 && (
          <div className="space-y-3">
            <input type="email" placeholder="Your account email" value={email} onChange={e => setEmail(e.target.value)}
              className={inputCls} />
            {err && <p className="text-red-500 text-xs">{err}</p>}
            <button onClick={handleSendOtp} disabled={busy}
              className="w-full bg-[#F47B20] hover:bg-[#D96A10] disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl text-sm transition-colors">
              {busy ? 'Sending...' : 'Send OTP'}
            </button>
          </div>
        )}

        {/* Step 2 — OTP */}
        {step === 2 && (
          <div className="space-y-3">
            <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g,''))}
              className={`${inputCls} text-center text-2xl font-black tracking-[12px]`} />
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
              <svg className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-xs text-amber-700">Can't find the email? Check your <span className="font-bold">spam or junk folder</span>.</p>
            </div>
            {err && <p className="text-red-500 text-xs text-center">{err}</p>}
            <button onClick={handleVerifyOtp} disabled={busy}
              className="w-full bg-[#F47B20] hover:bg-[#D96A10] disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl text-sm transition-colors">
              {busy ? 'Verifying...' : 'Verify OTP'}
            </button>
            <button onClick={handleSendOtp} disabled={busy} className="w-full text-xs text-gray-400 hover:text-[#F47B20] transition-colors py-1">
              Resend OTP
            </button>
          </div>
        )}

        {/* Step 3 — new password */}
        {step === 3 && (
          <div className="space-y-3">
            <div className="relative">
              <input type={showP ? 'text' : 'password'} placeholder="New password" value={pass}
                onChange={e => setPass(e.target.value)} className={`${inputCls} pr-12`} />
              <button type="button" onClick={() => setShowP(v => !v)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <EyeOffIcon className="w-4 h-4" />
              </button>
            </div>
            <input type={showP ? 'text' : 'password'} placeholder="Confirm password" value={confirm}
              onChange={e => setConfirm(e.target.value)} className={inputCls} />
            {err && <p className="text-red-500 text-xs">{err}</p>}
            <button onClick={handleReset} disabled={busy}
              className="w-full bg-[#F47B20] hover:bg-[#D96A10] disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl text-sm transition-colors">
              {busy ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Login Page ───────────────────────────────────────────────────────────
export default function LoginPage({ onNavigate, onUserLogin }) {
  const [showPass, setShowPass]       = useState(false);
  const [remember, setRemember]       = useState(false);
  const [form, setForm]               = useState({ email: '', password: '' });
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [showForgot, setShowForgot]   = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login(form.email, form.password);
      localStorage.setItem('auth_token', res.access_token);
      localStorage.setItem('user', JSON.stringify(res.user));
      if (onUserLogin) onUserLogin(res.user);
      if (res.user.role && res.user.role !== 'customer') {
        onNavigate('admin');
      } else {
        onNavigate('shop');
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 422) {
        setError('Invalid email or password. Please try again.');
      } else if (err.response?.status === 429) {
        setError('Too many login attempts. Please wait a minute and try again.');
      } else {
        setError('Something went wrong. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} />}

      <div className="min-h-screen bg-[#F2F4F7] lg:grid lg:grid-cols-[45%_55%]">
        <BrandPanel />

        <div className="flex flex-col min-h-screen lg:min-h-0">
          <div className="lg:hidden flex items-center px-4 pt-6 pb-2">
            <button onClick={() => onNavigate('shop')} className="p-2 -ml-2 text-[#F47B20]">
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 flex flex-col lg:justify-center px-4 md:px-12 lg:px-16 pb-8 lg:py-12">
            <button onClick={() => onNavigate('shop')} className="hidden lg:flex items-center gap-1 text-gray-400 hover:text-gray-600 mb-8 text-sm">
              <ChevronLeftIcon className="w-4 h-4" /> Back to shop
            </button>

            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-black text-[#1A1A2E]">Welcome back</h1>
              <p className="text-gray-500 text-sm mt-1.5">Please enter your details to login</p>
            </div>

            {/* Fields card */}
            <div className="bg-white rounded-2xl p-5 md:p-6 shadow-sm mb-4 space-y-3">
              <input name="email" value={form.email} onChange={handleChange}
                placeholder="Email Address" type="email"
                className="w-full px-4 py-3.5 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] placeholder-gray-400 outline-none focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all"
              />
              <div className="relative">
                <input name="password" value={form.password} onChange={handleChange}
                  placeholder="Password" type={showPass ? 'text' : 'password'}
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 text-sm text-[#1A1A2E] placeholder-gray-400 outline-none focus:border-[#F47B20] focus:ring-2 focus:ring-[#F47B20]/10 transition-all pr-12"
                />
                <button type="button" onClick={() => setShowPass(v => !v)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <EyeOffIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Remember + forgot */}
            <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => setRemember(v => !v)}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${remember ? 'bg-[#F47B20] border-[#F47B20]' : 'border-gray-300 bg-white'}`}
                >
                  {remember && (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
                <span className="text-sm text-gray-600">Remember me</span>
              </div>
              <div className="flex items-center justify-end">
                <button onClick={() => setShowForgot(true)} className="text-sm font-medium text-gray-700 hover:text-[#F47B20] transition-colors">
                  Forgot password?
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2.5 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-600 text-sm font-medium">{error}</p>
              </div>
            )}

            <button onClick={handleSubmit} disabled={loading} className="w-full bg-[#F47B20] hover:bg-[#D96A10] text-white font-bold py-4 rounded-2xl text-base transition-colors shadow-sm mb-4 disabled:opacity-70">
              {loading ? 'Logging In...' : 'Log In'}
            </button>

            <p className="text-center text-sm text-gray-500">
              Don't have an account?{' '}
              <button onClick={() => onNavigate('signup')} className="text-[#F47B20] font-semibold">Sign Up</button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
